import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  ApartmentOutlined as EmployerIcon,
  DeleteOutline as RemoveIcon,
  MoreVert as MoreIcon,
  OpenInNew as NewTabIcon,
  PeopleAltOutlined as AgencyIcon,
  Schedule as IntervalIcon,
  Search as SearchIcon,
  Sync as RefreshIcon,
} from '@mui/icons-material';
import { jobSourcesApi } from '../../services/api';
import { AtsPlatform, BoardAgencyFilter, BoardSort, BoardStateFilter, SourceBoard } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDateTime } from '../../utils/format';
import { atsLabel, externalUrl, normalizeText } from '../../utils/jobBoard';
import {
  ATS_PLATFORMS,
  BOARD_AGENCY_FILTERS,
  BOARD_AGENCY_LABELS,
  BOARD_SORTS,
  BOARD_SORT_LABELS,
  BOARD_STATES,
  BOARD_STATE_LABELS,
  BoardListState,
  DEFAULT_BOARD_LIST,
  agencyHint,
  boardInterval,
  boardListParams,
  boardListSearch,
  boardPages,
  countLabel,
  discoveryLabel,
  formatCount,
  intervalLabel,
  parseBoardListQuery,
  readAgo,
} from '../../utils/jobSources';
import ConfirmDialog from '../common/ConfirmDialog';
import DebouncedTextField from '../jobs/DebouncedTextField';
import BoardIntervalDialog from './BoardIntervalDialog';

interface Props {
  /** Changes when something outside this table changed the boards (a run, new settings, a new board). */
  reloadKey: number;
  /** The global refresh interval, for boards without their own. */
  globalHours: number;
  /** A board changed here: the numbers at the top of the page are out of date. */
  onChanged: () => void;
  onAdd: () => void;
  notify: (message: string, severity?: AlertColor) => void;
}

type Busy = 'toggle' | 'refresh' | 'remove' | 'agency';

interface RowProps {
  board: SourceBoard;
  busy: Busy | undefined;
  globalHours: number;
  onToggle: (board: SourceBoard, on: boolean) => void;
  onRefresh: (board: SourceBoard) => void;
  onMenu: (board: SourceBoard, anchor: HTMLElement) => void;
}

const SKELETON_ROWS = 6;

const boardName = (board: SourceBoard): string => board.company_name || board.board_token;

const TokenLink: React.FC<{ board: SourceBoard }> = ({ board }) => {
  const href = externalUrl(board.careers_url);
  if (!href) {
    return (
      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', overflowWrap: 'anywhere' }}>
        {board.board_token}
      </Box>
    );
  }
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${board.board_token}, the ${atsLabel(board.platform)} board of ${boardName(board)}, opens in a new tab`}
      sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', overflowWrap: 'anywhere' }}
    >
      {board.board_token}
      <NewTabIcon sx={{ fontSize: 12, ml: 0.25, verticalAlign: 'middle' }} />
    </Link>
  );
};

/** Marks a staffing agency or job marketplace, with why on hover; nothing for a direct employer. */
const AgencyTag: React.FC<{ board: SourceBoard }> = ({ board }) => {
  if (!board.is_staffing_agency) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.75, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
      <Tooltip title={agencyHint(board)} describeChild>
        <Chip size="small" variant="outlined" label="Agency" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 400 }} />
      </Tooltip>
      {board.agency_source === 'manual' && (
        <Typography variant="caption" component="span" color="text.secondary" sx={{ fontWeight: 400 }}>
          set by staff
        </Typography>
      )}
    </Box>
  );
};

const LastRead: React.FC<{ board: SourceBoard }> = ({ board }) =>
  board.last_scraped_at ? (
    <Tooltip title={formatDateTime(board.last_scraped_at)}>
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {readAgo(board.last_scraped_at)}
      </Box>
    </Tooltip>
  ) : (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      never
    </Box>
  );

const IntervalText: React.FC<{ board: SourceBoard; globalHours: number }> = ({ board, globalHours }) => {
  const interval = boardInterval(board.refresh_interval_hours, globalHours);
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      {intervalLabel(interval.hours)}{' '}
      <Box component="span" sx={{ color: interval.own ? 'primary.main' : 'text.secondary', fontWeight: interval.own ? 600 : 400 }}>
        ({interval.own ? 'own' : 'global'})
      </Box>
    </Box>
  );
};

/** Due, failing, and the last error, when there is anything to say. */
const Health: React.FC<{ board: SourceBoard }> = ({ board }) => {
  const failures = board.consecutive_failures;
  const troubled = failures > 0 || Boolean(board.last_error);
  if (!troubled && !board.is_due) {
    return (
      <Typography variant="body2" component="span" color="text.secondary">
        {board.is_active ? 'OK' : 'Off'}
      </Typography>
    );
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {board.is_due && <Chip size="small" variant="outlined" color="info" label="Due" />}
        {failures > 0 && (
          <Chip size="small" color={board.is_active ? 'warning' : 'error'} label={countLabel(failures, 'failure')} />
        )}
      </Stack>
      {board.last_error && (
        <Typography
          variant="caption"
          color="error"
          title={board.last_error}
          sx={{ display: 'block', mt: 0.25, maxWidth: 260, overflowWrap: 'anywhere' }}
        >
          {board.last_error}
        </Typography>
      )}
    </Box>
  );
};

const OnSwitch: React.FC<Pick<RowProps, 'board' | 'busy' | 'onToggle'>> = ({ board, busy, onToggle }) => (
  <Switch
    size="small"
    checked={board.is_active}
    disabled={Boolean(busy)}
    onChange={(e) => onToggle(board, e.target.checked)}
    inputProps={{ 'aria-label': `Read the ${atsLabel(board.platform)} board of ${boardName(board)}` }}
  />
);

const RowActions: React.FC<Pick<RowProps, 'board' | 'busy' | 'onRefresh' | 'onMenu'>> = ({ board, busy, onRefresh, onMenu }) => {
  const refreshLabel = board.is_active ? 'Refresh now' : 'Switch on and refresh now';
  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end">
      {busy === 'refresh' ? (
        <Box sx={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={18} aria-label={`Reading ${boardName(board)}`} />
        </Box>
      ) : (
        <Tooltip title={refreshLabel}>
          <span>
            <IconButton
              size="small"
              disabled={Boolean(busy)}
              onClick={() => onRefresh(board)}
              aria-label={`${refreshLabel}: ${boardName(board)}`}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <IconButton
        size="small"
        disabled={Boolean(busy)}
        onClick={(e) => onMenu(board, e.currentTarget)}
        aria-label={`More actions for ${boardName(board)}`}
        aria-haspopup="menu"
      >
        <MoreIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

const BoardRow: React.FC<RowProps> = ({ board, busy, globalHours, onToggle, onRefresh, onMenu }) => (
  <TableRow hover aria-busy={Boolean(busy)} sx={{ '& td, & th': { color: board.is_active ? undefined : 'text.secondary' } }}>
    <TableCell component="th" scope="row" sx={{ fontWeight: 600, maxWidth: 240, overflowWrap: 'anywhere' }}>
      {boardName(board)}
      <AgencyTag board={board} />
    </TableCell>
    <TableCell>{atsLabel(board.platform)}</TableCell>
    <TableCell sx={{ maxWidth: 200 }}>
      <TokenLink board={board} />
    </TableCell>
    <TableCell>
      <OnSwitch board={board} busy={busy} onToggle={onToggle} />
    </TableCell>
    <TableCell align="right">{formatCount(board.job_count)}</TableCell>
    <TableCell>
      <LastRead board={board} />
    </TableCell>
    <TableCell>
      <IntervalText board={board} globalHours={globalHours} />
    </TableCell>
    <TableCell>
      <Health board={board} />
    </TableCell>
    <TableCell sx={{ whiteSpace: 'nowrap' }}>{discoveryLabel(board.discovered_via)}</TableCell>
    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
      <RowActions board={board} busy={busy} onRefresh={onRefresh} onMenu={onMenu} />
    </TableCell>
  </TableRow>
);

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="dt">
      {label}
    </Typography>
    <Typography variant="body2" component="dd" sx={{ m: 0 }}>
      {children}
    </Typography>
  </Box>
);

/** The same board as a card, for phone widths. */
const BoardCard: React.FC<RowProps> = ({ board, busy, globalHours, onToggle, onRefresh, onMenu }) => (
  <Paper component="li" variant="outlined" aria-busy={Boolean(busy)} sx={{ p: 1.5, listStyle: 'none' }}>
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
          {boardName(board)}
          <AgencyTag board={board} />
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {atsLabel(board.platform)}: <TokenLink board={board} />
        </Typography>
      </Box>
      <OnSwitch board={board} busy={busy} onToggle={onToggle} />
    </Stack>
    <Box
      component="dl"
      sx={{ m: 0, mt: 1, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.5, rowGap: 0.75 }}
    >
      <Fact label="Jobs">{formatCount(board.job_count)}</Fact>
      <Fact label="Last read">
        <LastRead board={board} />
      </Fact>
      <Fact label="Interval">
        <IntervalText board={board} globalHours={globalHours} />
      </Fact>
      <Fact label="Found via">{discoveryLabel(board.discovered_via)}</Fact>
    </Box>
    <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mt: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Health board={board} />
      </Box>
      <RowActions board={board} busy={busy} onRefresh={onRefresh} onMenu={onMenu} />
    </Stack>
  </Paper>
);

/**
 * The registry of company boards: search, filters, sort and pages come from
 * the API and are held in the URL query string, like the job board's.
 */
const BoardsSection: React.FC<Props> = ({ reloadKey, globalHours, onChanged, onAdd, notify }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  // ---- URL <-> state --------------------------------------------------------

  const listKey = useMemo(() => boardListSearch(parseBoardListQuery(location.search)), [location.search]);
  const state = useMemo(() => parseBoardListQuery(listKey), [listKey]);
  const latest = useRef({ state, pathname: location.pathname });
  latest.current = { state, pathname: location.pathname };

  /** Change a filter, the sort or the page. Anything but the page goes back to page 1. */
  const update = useCallback(
    (patch: Partial<BoardListState>, options?: { replace?: boolean }) => {
      const current = latest.current;
      const next: BoardListState = { ...current.state, page: 1, ...patch };
      const search = boardListSearch(next);
      if (search === boardListSearch(current.state)) return;
      latest.current = { ...current, state: next };
      navigate({ pathname: current.pathname, search }, { replace: options?.replace });
    },
    [navigate]
  );
  const commitSearch = useCallback((value: string) => update({ q: value }, { replace: true }), [update]);

  // ---- rows -----------------------------------------------------------------

  const [boards, setBoards] = useState<SourceBoard[] | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localReload, setLocalReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    jobSourcesApi
      .boards(boardListParams(state), controller.signal)
      .then((data) => {
        const results = Array.isArray(data?.results) ? data.results : [];
        setBoards(results);
        setCount(typeof data?.count === 'number' ? data.count : results.length);
        setLoading(false);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        if ((e as { response?: { status?: number } })?.response?.status === 404 && state.page > 1) {
          // A page past the end: the list shrank (a board was removed, say).
          update({ page: 1 }, { replace: true });
          return;
        }
        setError(errorMessage(e, 'Could not load the boards. Please try again.'));
        setLoading(false);
      });
    return () => controller.abort();
  }, [state, reloadKey, localReload, update]);

  // ---- row actions ----------------------------------------------------------

  const [busy, setBusy] = useState<Record<string, Busy>>({});
  const [menu, setMenu] = useState<{ board: SourceBoard; anchor: HTMLElement } | null>(null);
  // Keep the menu's wording while it closes.
  const lastMenuBoard = useRef<SourceBoard | null>(null);
  if (menu) lastMenuBoard.current = menu.board;
  const menuIsAgency = (menu?.board ?? lastMenuBoard.current)?.is_staffing_agency === true;
  const [intervalBoard, setIntervalBoard] = useState<SourceBoard | null>(null);
  const [removeBoard, setRemoveBoard] = useState<SourceBoard | null>(null);
  const [removing, setRemoving] = useState(false);
  // Keep the board in the confirmation while it closes.
  const lastRemove = useRef<SourceBoard | null>(null);
  if (removeBoard) lastRemove.current = removeBoard;
  const shownRemove = removeBoard ?? lastRemove.current;

  const markBusy = useCallback((id: string, kind: Busy | null) => {
    setBusy((prev) => {
      const next = { ...prev };
      if (kind) next[id] = kind;
      else delete next[id];
      return next;
    });
  }, []);

  const replaceBoard = useCallback((board: SourceBoard) => {
    setBoards((prev) => (prev ? prev.map((row) => (row.id === board.id ? board : row)) : prev));
  }, []);

  const toggle = useCallback(
    async (board: SourceBoard, on: boolean) => {
      const name = boardName(board);
      markBusy(board.id, 'toggle');
      try {
        const { board: next, jobs_hidden } = await jobSourcesApi.patchBoard(board.id, { is_active: on });
        replaceBoard(next);
        notify(
          on
            ? `${name} switched on. It is due now and will be read on the next refresh.`
            : `${name} switched off. ${countLabel(jobs_hidden ?? 0, 'listing')} hidden from the job board.`
        );
        onChanged();
      } catch (e) {
        notify(errorMessage(e, `Could not switch ${name} ${on ? 'on' : 'off'}.`), 'error');
      } finally {
        markBusy(board.id, null);
      }
    },
    [markBusy, replaceBoard, notify, onChanged]
  );

  const refresh = useCallback(
    async (board: SourceBoard) => {
      const name = boardName(board);
      markBusy(board.id, 'refresh');
      try {
        const { board: next, result } = await jobSourcesApi.refreshBoard(board.id);
        replaceBoard(next);
        if (result?.error) {
          notify(
            `Could not read ${name}: ${result.error}.${next.is_active ? '' : ' The board was switched off.'}`,
            'error'
          );
        } else {
          notify(
            `${name} read: ${countLabel(result?.active ?? 0, 'listing')} (${formatCount(result?.created)} new, ${formatCount(
              result?.updated
            )} updated, ${formatCount(result?.deactivated)} retired).`
          );
        }
        onChanged();
      } catch (e) {
        notify(errorMessage(e, `Could not refresh ${name}.`), 'error');
      } finally {
        markBusy(board.id, null);
      }
    },
    [markBusy, replaceBoard, notify, onChanged]
  );

  /** Staff's own call on whether the company is a staffing agency; refreshes keep it. */
  const setAgency = useCallback(
    async (board: SourceBoard, isAgency: boolean) => {
      const name = boardName(board);
      const kind = isAgency ? 'a staffing agency' : 'a direct employer';
      markBusy(board.id, 'agency');
      try {
        const { board: next } = await jobSourcesApi.setBoardAgency(board.id, isAgency);
        replaceBoard(next);
        notify(
          isAgency
            ? `${name} marked as ${kind}. The job board hides its listings unless agencies are included.`
            : `${name} marked as ${kind}. Its listings show on the job board like any other.`
        );
        onChanged();
      } catch (e) {
        notify(errorMessage(e, `Could not mark ${name} as ${kind}.`), 'error');
      } finally {
        markBusy(board.id, null);
      }
    },
    [markBusy, replaceBoard, notify, onChanged]
  );

  const openMenu = useCallback((board: SourceBoard, anchor: HTMLElement) => setMenu({ board, anchor }), []);

  const confirmRemove = async () => {
    const board = removeBoard;
    if (!board) return;
    const name = boardName(board);
    setRemoving(true);
    markBusy(board.id, 'remove');
    try {
      const { jobs_hidden } = await jobSourcesApi.removeBoard(board.id);
      setBoards((prev) => (prev ? prev.filter((row) => row.id !== board.id) : prev));
      setCount((prev) => (prev === null ? prev : Math.max(0, prev - 1)));
      notify(`${name} removed. ${countLabel(jobs_hidden ?? 0, 'listing')} left the job board.`);
      setRemoveBoard(null);
      setLocalReload((key) => key + 1);
      onChanged();
    } catch (e) {
      notify(errorMessage(e, `Could not remove ${name}.`), 'error');
    } finally {
      setRemoving(false);
      markBusy(board.id, null);
    }
  };

  // A link from the per-ATS table lands here (#sources-boards): bring the list into view.
  useEffect(() => {
    if (location.hash === '#sources-boards') document.getElementById('sources-boards')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, location.search]);

  // ---- render ---------------------------------------------------------------

  const filtered = Boolean(state.q || state.platform || state.agency || state.state);
  const pages = count === null ? 1 : boardPages(count);
  const countText = count === null ? '' : `${countLabel(count, 'board')}${filtered ? (count === 1 ? ' matches' : ' match') : ''}`;
  const rowProps = { globalHours, onToggle: toggle, onRefresh: refresh, onMenu: openMenu };

  return (
    <Paper id="sources-boards" variant="outlined" component="section" aria-labelledby="sources-boards-title" sx={{ p: 2, scrollMarginTop: 80 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography id="sources-boards-title" variant="h6" component="h2" sx={{ fontWeight: 700, flex: 1 }}>
          Boards
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}>
          Add board
        </Button>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'minmax(180px, 1fr) repeat(4, minmax(140px, 170px))' },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <DebouncedTextField
          value={state.q}
          onCommit={commitSearch}
          normalize={normalizeText}
          placeholder="Search by company or token"
          inputProps={{ 'aria-label': 'Search boards by company or token' }}
          size="small"
          fullWidth
          clearLabel="Clear search"
          startAdornment={<SearchIcon fontSize="small" color="action" />}
          sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
        />
        <TextField
          select
          label="ATS"
          size="small"
          value={state.platform}
          onChange={(e) => update({ platform: e.target.value as AtsPlatform | '' })}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">All</MenuItem>
          {ATS_PLATFORMS.map((platform) => (
            <MenuItem key={platform} value={platform}>
              {atsLabel(platform)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Type"
          size="small"
          value={state.agency}
          onChange={(e) => update({ agency: e.target.value as BoardAgencyFilter | '' })}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">All</MenuItem>
          {BOARD_AGENCY_FILTERS.map((value) => (
            <MenuItem key={value} value={value}>
              {BOARD_AGENCY_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="State"
          size="small"
          value={state.state}
          onChange={(e) => update({ state: e.target.value as BoardStateFilter | '' })}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">All</MenuItem>
          {BOARD_STATES.map((value) => (
            <MenuItem key={value} value={value}>
              {BOARD_STATE_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Sort by"
          size="small"
          value={state.sort}
          onChange={(e) => update({ sort: e.target.value as BoardSort })}
        >
          {BOARD_SORTS.map((sort) => (
            <MenuItem key={sort} value={sort}>
              {BOARD_SORT_LABELS[sort]}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 32 }}>
        <Typography variant="subtitle2" component="p" role="status" aria-live="polite" sx={{ fontWeight: 600 }}>
          {error ? '' : count === null ? <Skeleton width={90} /> : countText}
        </Typography>
        {filtered && (
          <Button size="small" onClick={() => update({ ...DEFAULT_BOARD_LIST, sort: state.sort })}>
            Clear filters
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {!error && count !== null && pages > 1 && (
          <Typography variant="caption" color="text.secondary">
            Page {formatCount(Math.min(state.page, pages))} of {formatCount(pages)}
          </Typography>
        )}
      </Stack>
      <Box sx={{ height: 4, my: 0.5 }}>{loading && boards !== null && <LinearProgress aria-label="Loading boards" />}</Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setLocalReload((key) => key + 1)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : boards === null ? (
        <Stack spacing={1} aria-busy="true" aria-label="Loading boards">
          {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={isDesktop ? 44 : 150} />
          ))}
        </Stack>
      ) : boards.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
          <Typography variant="subtitle1" component="p" sx={{ fontWeight: 600 }}>
            {filtered ? 'No boards match' : 'No boards in the registry yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered ? 'Try a different search or clear the filters.' : 'Add a company board to start filling the job board.'}
          </Typography>
        </Box>
      ) : isDesktop ? (
        <TableContainer sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <Table size="small" aria-labelledby="sources-boards-title" aria-busy={loading}>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>ATS</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>On</TableCell>
                <TableCell align="right">Jobs</TableCell>
                <TableCell>Last read</TableCell>
                <TableCell>Interval</TableCell>
                <TableCell>Health</TableCell>
                <TableCell>Found via</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boards.map((board) => (
                <BoardRow key={board.id} board={board} busy={busy[board.id]} {...rowProps} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          component="ul"
          aria-labelledby="sources-boards-title"
          aria-busy={loading}
          sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1, opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}
        >
          {boards.map((board) => (
            <BoardCard key={board.id} board={board} busy={busy[board.id]} {...rowProps} />
          ))}
        </Box>
      )}

      {!error && boards !== null && boards.length > 0 && pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pages}
            page={Math.min(state.page, pages)}
            siblingCount={isDesktop ? 1 : 0}
            size={isDesktop ? 'medium' : 'small'}
            color="primary"
            renderItem={(item) => (
              <PaginationItem
                component={RouterLink}
                to={{ pathname: location.pathname, search: boardListSearch({ ...state, page: item.page ?? 1 }) }}
                {...item}
              />
            )}
          />
        </Box>
      )}

      <Menu anchorEl={menu?.anchor ?? null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            if (menu) setIntervalBoard(menu.board);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <IntervalIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Set refresh interval</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) setAgency(menu.board, !menu.board.is_staffing_agency);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            {menuIsAgency ? <EmployerIcon fontSize="small" /> : <AgencyIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuIsAgency ? 'Mark as direct employer' : 'Mark as staffing agency'}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) setRemoveBoard(menu.board);
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <RemoveIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>Remove from the registry</ListItemText>
        </MenuItem>
      </Menu>

      <BoardIntervalDialog
        board={intervalBoard}
        globalHours={globalHours}
        onClose={() => setIntervalBoard(null)}
        onSaved={(next) => {
          replaceBoard(next);
          setIntervalBoard(null);
          const interval = boardInterval(next.refresh_interval_hours, globalHours);
          notify(
            interval.own
              ? `${boardName(next)} is now read every ${intervalLabel(interval.hours)}.`
              : `${boardName(next)} is back on the global interval (${intervalLabel(interval.hours)}).`
          );
          onChanged();
        }}
      />

      <ConfirmDialog
        open={Boolean(removeBoard)}
        title={`Remove ${shownRemove ? boardName(shownRemove) : 'this board'}?`}
        message={
          shownRemove
            ? `This takes the ${atsLabel(shownRemove.platform)} board "${shownRemove.board_token}" out of the registry. Its listings leave the job board at once (${countLabel(
                shownRemove.job_count,
                'listing'
              )} at the last read) and it is not read again. To keep the board but stop reading it, switch it off instead.`
            : undefined
        }
        confirmLabel="Remove board"
        confirmColor="error"
        loading={removing}
        onConfirm={confirmRemove}
        onCancel={() => setRemoveBoard(null)}
      />
    </Paper>
  );
};

export default BoardsSection;
